import TurndownService from "turndown";

// Configure turndown for our specific use case
const turndownService = new TurndownService({
    headingStyle: "atx",
    codeBlockStyle: "fenced",
    emDelimiter: "*",
    bulletListMarker: "-",
    strongDelimiter: "**",
    linkStyle: "inlined",
    linkReferenceStyle: "full",
});

// Add custom rules for better conversion
turndownService.addRule("strikethrough", {
    filter: ["del", "s"],
    replacement: function (content: string) {
        return "~~" + content + "~~";
    },
});

turndownService.addRule("underline", {
    filter: "u",
    replacement: function (content: string) {
        return "++" + content + "++";
    },
});

// Add rule for indented content with custom syntax
turndownService.addRule("indentedContent", {
    filter: function (node: Element) {
        // Check for elements with margin-left, padding-left, or text-indent
        const style = (node as HTMLElement).style || {};
        const hasIndentation =
            (style.marginLeft && parseInt(style.marginLeft) > 0) ||
            (style.paddingLeft && parseInt(style.paddingLeft) > 0) ||
            (style.textIndent && parseInt(style.textIndent) > 0);

        // Also check for common indentation classes
        const className = node.className || "";
        const hasIndentClass = /indent|margin|padding/.test(className);

        return hasIndentation || hasIndentClass;
    },
    replacement: function (content: string, node: any) {
        const style = (node as HTMLElement).style || {};
        let indentLevel = 0;

        // Check margin-left
        if (style.marginLeft) {
            // each level indendent is 2em
            const marginValue = parseInt(style.marginLeft);
            if (!isNaN(marginValue)) {
                indentLevel = marginValue / 2;
            }
        }

        // Ensure minimum indent level of 1
        indentLevel = Math.max(1, indentLevel);

        // Add custom indentation syntax: {indent:level} at the start of each line
        const lines = content.split("\n");
        const indentedLines = lines.map((line) => {
            if (line.trim()) {
                return `{indent:${indentLevel}}${line}`;
            }
            return line;
        });
        return indentedLines.join("\n") + "\n";
    },
});

// Helper to extract text from a table cell, escaping pipes and collapsing newlines
function getCellText(cell: Element): string {
    return (cell.textContent || "").replace(/\|/g, "\\|").replace(/\n/g, " ").trim();
}

// GFM table rule: converts <table> to pipe-table markdown
turndownService.addRule("gfmTable", {
    filter: "table",
    replacement: function (_content: string, node: any) {
        const table = node as HTMLTableElement;
        const thead = table.querySelector("thead");
        const tbody = table.querySelector("tbody");

        const headerCells: string[] = [];
        if (thead) {
            const headerRow = thead.querySelector("tr");
            if (headerRow) {
                for (const cell of Array.from(headerRow.children)) {
                    headerCells.push(getCellText(cell));
                }
            }
        }

        const bodyRows: string[][] = [];
        const rowSource = tbody || table;
        for (const row of Array.from(rowSource.querySelectorAll("tr"))) {
            // Skip rows already handled by thead
            if (thead && row.parentElement === thead) continue;

            const cells: string[] = [];
            for (const cell of Array.from(row.children)) {
                cells.push(getCellText(cell));
            }

            // If no explicit thead, use first row as header
            if (headerCells.length === 0 && bodyRows.length === 0) {
                headerCells.push(...cells);
                continue;
            }
            bodyRows.push(cells);
        }

        if (headerCells.length === 0) return "";

        // Compute column widths for padding
        const colCount = headerCells.length;
        const colWidths = headerCells.map((h) => h.length);
        for (const row of bodyRows) {
            for (let i = 0; i < colCount; i++) {
                const cellLen = (row[i] || "").length;
                if (cellLen > colWidths[i]) colWidths[i] = cellLen;
            }
        }
        // Minimum width of 3 for the separator
        for (let i = 0; i < colWidths.length; i++) {
            if (colWidths[i] < 3) colWidths[i] = 3;
        }

        const pad = (text: string, width: number) => text.padEnd(width);

        const headerLine =
            "| " + headerCells.map((h, i) => pad(h, colWidths[i])).join(" | ") + " |";
        const separatorLine = "| " + colWidths.map((w) => "-".repeat(w)).join(" | ") + " |";
        const bodyLines = bodyRows.map(
            (row) =>
                "| " +
                headerCells.map((_, i) => pad(row[i] || "", colWidths[i])).join(" | ") +
                " |",
        );

        return "\n\n" + [headerLine, separatorLine, ...bodyLines].join("\n") + "\n\n";
    },
});

// Ignore table child elements — the gfmTable rule handles everything via DOM traversal
turndownService.addRule("tableChildIgnore", {
    filter: ["thead", "tbody", "tfoot", "tr", "td", "th"] as any,
    replacement: function () {
        return "";
    },
});

// Add rule for preserving whitespace in pre-formatted content
turndownService.addRule("preserveWhitespace", {
    filter: ["pre", "code"],
    replacement: function (content: string) {
        // Preserve indentation in code blocks
        const lines = content.split("\n");
        const indentedLines = lines.map((line) => {
            // Count leading spaces/tabs
            const match = line.match(/^(\s+)/);
            if (match) {
                const indent = match[1];
                // Convert to consistent indentation (4 spaces)
                const indentLevel = Math.ceil(indent.length / 4);
                return "    ".repeat(indentLevel) + line.trimLeft();
            }
            return line;
        });
        return "```\n" + indentedLines.join("\n") + "\n```\n";
    },
});

// Add rule for list indentation
turndownService.addRule("listIndentation", {
    filter: ["ul", "ol"],
    replacement: function (content: string) {
        // Ensure proper indentation for nested lists
        const lines = content.split("\n");
        const indentedLines = lines.map((line) => {
            if (line.trim().match(/^[-*+]\s/)) {
                // This is a list item, ensure proper indentation
                return "  " + line;
            }
            return line;
        });
        return indentedLines.join("\n") + "\n";
    },
});

// Add rule for nested indentation within paragraphs and divs
turndownService.addRule("nestedIndentation", {
    filter: function (node: any) {
        // Check if this element has children with different indentation levels
        const children = node.children || [];
        return children.length > 0 && (node.tagName === "p" || node.tagName === "div");
    },
    replacement: function (content: string, node: any) {
        // Process content to handle nested indentation
        const lines = content.split("\n");
        const processedLines = lines.map((line) => {
            // If line already has indentation syntax, preserve it
            if (line.match(/^\{indent:\d+\}/)) {
                return line;
            }
            // Otherwise, check if it needs indentation based on parent context
            return line;
        });
        return processedLines.join("\n") + "\n";
    },
});

/**
 * Register a Turndown rule that converts the admin editor's embed placeholder
 * div — `<div data-blog-embed data-blog-embed-kind="…" data-blog-embed-url="…">` —
 * into the corresponding `:::kind{url="…"}` container directive. Emitting the
 * directive as a raw string bypasses Turndown's text escaping (which would
 * otherwise backslash-escape underscores in the URL) and preserves the newline
 * between the opening and closing fences that remark-directive requires.
 *
 * Called once at module-load time by the admin editor; safe to call repeatedly.
 */
let blogPostEmbedRuleRegistered = false;
export function registerBlogPostEmbedTurndownRule() {
    if (blogPostEmbedRuleRegistered) return;
    blogPostEmbedRuleRegistered = true;
    turndownService.addRule("blogPostEmbed", {
        filter: function (node) {
            return node.nodeName === "DIV" && (node as HTMLElement).hasAttribute("data-blog-embed");
        },
        replacement: function (_content, node) {
            const el = node as HTMLElement;
            const kind = el.getAttribute("data-blog-embed-kind") ?? "youtube";
            const url = el.getAttribute("data-blog-embed-url") ?? "";
            const variant = el.getAttribute("data-blog-embed-variant");
            const variantAttr = variant ? ` kind="${variant}"` : "";
            return `\n\n:::${kind}{url="${url}"${variantAttr}}\n:::\n\n`;
        },
    });
}

export function htmlToMarkdown(html: string): string {
    if (!html || html.trim() === "") {
        return "";
    }

    try {
        const markdown = turndownService.turndown(html);

        // Post-process to handle any remaining indentation patterns
        const processedMarkdown = postProcessIndentation(markdown);

        return processedMarkdown;
    } catch (error) {
        console.error("Error converting HTML to markdown:", error);
        // Fallback: return the original HTML if conversion fails
        return html;
    }
}

function postProcessIndentation(markdown: string): string {
    const lines = markdown.split("\n");
    const processedLines = lines.map((line) => {
        // Look for common indentation patterns that might have been missed
        const trimmedLine = line.trim();

        // Skip empty lines or lines that already have indentation syntax
        if (!trimmedLine || line.match(/^\{indent:\d+\}/)) {
            return line;
        }

        // Check for leading spaces that might indicate indentation
        const leadingSpaces = line.length - line.trimLeft().length;
        if (leadingSpaces > 0) {
            const indentLevel = Math.ceil(leadingSpaces / 4); // 4 spaces per level
            return `{indent:${indentLevel}}${trimmedLine}`;
        }

        return line;
    });

    return processedLines.join("\n");
}
